import { useMemo, useState, useCallback } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useDispatch, useSelector } from 'react-redux';
import {
  MobileStateQuestion,
  MobileStateTaskSelectListGroup,
  MobileStateTaskSelectTask,
} from '@safeguardproperties/corescript-react-npm/dist/types';
import { TaskScript } from '@safeguardproperties/corescript-react-npm/dist/types/taskScript';
import { AppDispatch, RootState } from '../../redux/Store';
import {
  addTaskByKey,
  deleteTaskByKey,
  setTaskAnswer,
  getTaskScript,
  getTasksData,
  getTaskState,
  getSavedTasks,
} from '../../redux/reducer/coreScriptReducer';
import { setIsGalleryPanelOpen } from '../../redux/reducer/contextIdReducer';
import CustomTypography from '../../ui/atoms/CustomTypography';
import QuestionItemView from '../Questions/QuestionItemView';
import TaskMediaView from './TaskMediaView';
import { taskAnchorId } from './TaskSidePanel';
import { headerCommonSx } from '../MainComponent/question-group';

const keyToString = (value?: string): string => value?.toLanguagePack() ?? '';

// Bid-mode tasks are surfaced under their own "Bids" tab; everything else stays
// under "Tasks".
const BID_MODES = new Set(['BidProposal', 'OrderedBidProposal']);

// Build the picker library for a single mode (e.g. 'Perform' for the Tasks tab,
// 'BidProposal' for the Bids tab). Each entry's id is `${type}~${mode}` so
// AddTaskByKey instantiates the task in exactly that mode. Mirrors the engine's
// generateTaskList shape, but scoped to one mode instead of "Perform or Bid".
const buildPickerList = (
  taskScript: TaskScript | undefined,
  modeKey: string
): MobileStateTaskSelectListGroup[] => {
  const groups = new Map<string, MobileStateTaskSelectListGroup>();
  for (const taskType of taskScript?.Types ?? []) {
    if (!taskType._active) {
      continue;
    }
    const mode = taskType.Modes?.find(
      (modeItem) => modeItem._active && modeItem.Key === modeKey
    );
    if (!mode) {
      continue;
    }
    const groupId = taskType.ParentCategoryName ?? '';
    const group =
      groups.get(groupId) ??
      ({
        group: { id: groupId, searchTitle: groupId.toLowerCase() },
        tasks: [],
      } as MobileStateTaskSelectListGroup);
    group.tasks.push({
      groupId,
      id: `${taskType._mapKey}~${mode._mapKey}`,
      title: taskType.DisplayName,
      searchTitle: taskType.DisplayName?.toLowerCase(),
    } as MobileStateTaskSelectTask);
    groups.set(groupId, group);
  }
  return Array.from(groups.values());
};

// Mirrors safeview-mobile getUniqueId: counts roll up to the task type segment
// (the part before the first '~'), so every added mode/instance increments the
// same library row.
const getUniqueId = (id: string): string => id.split('~')[0];

const countPillSx = {
  backgroundColor: '#6B8BFF',
  color: '#fff',
  fontFamily: 'Montserrat',
  fontSize: '12px',
  fontWeight: 600,
  borderRadius: '12px',
  padding: '2px 8px',
  whiteSpace: 'nowrap',
} as const;

type ModeCount = { bid: number; task: number };

// "Bid(bids)/Task(tasks)" for a picker row; empty when nothing of that type is added.
const formatBidTaskCount = (entry?: ModeCount): string =>
  entry && entry.bid + entry.task > 0
    ? `Bid(${entry.bid})/Task(${entry.task})`
    : '';

type TaskPickerDialogProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  taskList: MobileStateTaskSelectListGroup[];
  taskCountMap: Record<string, ModeCount>;
  onAddTask: (taskId: string) => void;
  disabled: boolean;
};

/**
 * TaskPickerDialog mirrors safeview-mobile's TaskPicker: a searchable library of
 * tasks grouped into collapsible category sections, with per-task and per-group
 * count badges and a '+' control that adds a task while keeping the picker open
 * so multiple instances can be added in one pass.
 */
const TaskPickerDialog = ({
  open,
  onClose,
  title,
  taskList,
  taskCountMap,
  onAddTask,
  disabled,
}: TaskPickerDialogProps) => {
  const [search, setSearch] = useState('');
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({});

  const searchKey = useMemo(() => search.trim().toLowerCase(), [search]);

  const taskSearchTitle = useCallback(
    (task: MobileStateTaskSelectTask) =>
      task.searchTitle ?? keyToString(task.title).toLowerCase(),
    []
  );

  // When searching, auto-expand any section that has a matching task so the
  // results are visible without a manual tap (matches mobile behavior).
  const autoExpanded = useMemo(() => {
    const expanded: Record<string, boolean> = {};
    if (!searchKey) {
      return expanded;
    }
    for (const section of taskList) {
      if (
        section.tasks?.some((task) => taskSearchTitle(task).includes(searchKey))
      ) {
        expanded[section.group.id] = true;
      }
    }
    return expanded;
  }, [taskList, searchKey, taskSearchTitle]);

  const isSectionExpanded = useCallback(
    (id: string) => (searchKey ? !!autoExpanded[id] : !!expandedSections[id]),
    [searchKey, autoExpanded, expandedSections]
  );

  const toggleSection = useCallback((id: string) => {
    setExpandedSections((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const sections = useMemo(() => {
    return taskList
      .map((section) => {
        const groupId = section.group.id;
        const groupSearch = section.group.searchTitle ?? groupId.toLowerCase();
        const groupMatch = !!searchKey && groupSearch.includes(searchKey);
        const expanded = isSectionExpanded(groupId);
        const tasks = section.tasks ?? [];

        const data =
          expanded && searchKey
            ? tasks.filter((task) => taskSearchTitle(task).includes(searchKey))
            : expanded
              ? tasks
              : [];

        return { group: section.group, expanded, data, groupMatch };
      })
      .filter(
        (section) => !searchKey || section.groupMatch || section.data.length > 0
      );
  }, [taskList, searchKey, isSectionExpanded, taskSearchTitle]);

  const hasResults = sections.length > 0;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='sm'>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontFamily: 'Montserrat',
          fontWeight: 600,
        }}
      >
        {title}
        <IconButton size='small' onClick={onClose}>
          <CloseIcon fontSize='small' />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <TextField
          fullWidth
          size='small'
          placeholder='Search tasks...'
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ marginBottom: '12px' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon fontSize='small' />
              </InputAdornment>
            ),
          }}
        />

        {!hasResults && (
          <CustomTypography
            variant='body1'
            sx={{
              fontFamily: 'Montserrat',
              fontSize: '14px',
              color: '#999',
              textAlign: 'center',
              padding: '24px 0',
            }}
          >
            No tasks found
          </CustomTypography>
        )}

        {sections.map((section) => {
          const groupCount = formatBidTaskCount(taskCountMap[section.group.id]);
          return (
            <Box key={section.group.id} sx={{ marginBottom: '8px' }}>
              <Box
                onClick={() => toggleSection(section.group.id)}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  padding: '12px 14px',
                  border: '1px solid #E3E3E3',
                  borderRadius: '8px',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CustomTypography
                    variant='body1'
                    sx={{
                      fontFamily: 'Montserrat',
                      fontSize: '15px',
                      fontWeight: section.expanded ? 700 : 600,
                      color: '#343330',
                    }}
                  >
                    {section.group.id}
                  </CustomTypography>
                  {groupCount && <Box sx={countPillSx}>{groupCount}</Box>}
                </Box>
                {section.expanded ? (
                  <KeyboardArrowDownIcon />
                ) : (
                  <KeyboardArrowUpIcon />
                )}
              </Box>

              <Collapse in={section.expanded} unmountOnExit>
                {section.data.map((task) => {
                  const taskCount = formatBidTaskCount(
                    taskCountMap[getUniqueId(task.id)]
                  );
                  return (
                    <Box
                      key={task.id}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '10px 14px',
                        borderLeft: '1px solid #E3E3E3',
                        borderRight: '1px solid #E3E3E3',
                        borderBottom: '1px solid #E3E3E3',
                      }}
                    >
                      <CustomTypography
                        variant='body1'
                        sx={{
                          fontFamily: 'Montserrat',
                          fontSize: '14px',
                          color: '#343330',
                        }}
                      >
                        {keyToString(task.title)}
                      </CustomTypography>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        {taskCount && <Box sx={countPillSx}>{taskCount}</Box>}
                        <IconButton
                          size='small'
                          disabled={disabled}
                          onClick={() => onAddTask(task.id)}
                          sx={{
                            backgroundColor: '#53b50a',
                            color: '#fff',
                            '&:hover': { backgroundColor: '#388007' },
                            '&.Mui-disabled': { backgroundColor: '#C7C7C7' },
                          }}
                        >
                          <AddIcon fontSize='small' />
                        </IconButton>
                      </Box>
                    </Box>
                  );
                })}
              </Collapse>
            </Box>
          );
        })}
      </DialogContent>
    </Dialog>
  );
};

/**
 * TaskControl renders the task-library picker plus each added task's questions
 * for the non-audit INSPI flow. Mirrors the data model of safeview-mobile's
 * Tasks reducer: available tasks come from the engine-built TaskList, added
 * tasks from tasksData/taskState, and answers persist into saveData.Tasks via
 * SetTaskAnswer.
 */
type TaskControlProps = {
  // 'bids' shows only BidProposal/OrderedBidProposal tasks; 'tasks' shows the rest.
  mode?: 'tasks' | 'bids';
  // When false (e.g. audit-review contexts) the Add action is hidden and the
  // tab only displays already-submitted tasks/bids.
  allowAdd?: boolean;
};

const TaskControl = ({ mode = 'tasks', allowAdd = true }: TaskControlProps) => {
  const dispatch = useDispatch<AppDispatch>();

  const taskScript = useSelector(getTaskScript);
  const tasksData = useSelector(getTasksData);
  const taskState = useSelector(getTaskState);
  const savedTasks = useSelector(getSavedTasks);
  const isReadOnly = useSelector(
    (state: RootState) => state.contextId.isReadOnly
  );

  const isBids = mode === 'bids';
  const heading = isBids ? 'Bids' : 'Tasks';
  const addLabel = isBids ? 'Add Bid' : 'Add Task';
  const emptyText = isBids
    ? 'No bids added yet. Use “Add Bid” to start one.'
    : 'No tasks added yet. Use “Add Task” to start one.';

  // Bids tab offers BidProposal modes; Tasks tab offers Perform modes.
  const taskList = useMemo(
    () => buildPickerList(taskScript, isBids ? 'BidProposal' : 'Perform'),
    [taskScript, isBids]
  );

  const [pickerOpen, setPickerOpen] = useState(false);

  // Only the added tasks whose mode matches this tab (bid vs non-bid).
  const addedTaskIds = useMemo(
    () =>
      Object.keys(tasksData ?? {}).filter((taskId) => {
        const modeType = taskState[taskId]?.modeType;
        const taskIsBid = !!modeType && BID_MODES.has(modeType);
        return isBids ? taskIsBid : !taskIsBid;
      }),
    [tasksData, taskState, isBids]
  );

  const hasAvailableTasks = useMemo(
    () => taskList?.some((group) => (group?.tasks?.length ?? 0) > 0),
    [taskList]
  );

  // Count map mirroring safeview-mobile's taskCountMap: every added instance
  // increments both its task-type segment and its category, so the picker can
  // show how many of each are already on the order.
  const taskCountMap = useMemo(() => {
    const map: Record<string, ModeCount> = {};
    const bump = (key: string, taskIsBid: boolean) => {
      const entry = (map[key] ??= { bid: 0, task: 0 });
      if (taskIsBid) {
        entry.bid += 1;
      } else {
        entry.task += 1;
      }
    };
    // Count every added task (both tabs share one picker library), split by mode.
    for (const key of Object.keys(tasksData ?? {})) {
      const modeType = taskState[key]?.modeType;
      const taskIsBid = !!modeType && BID_MODES.has(modeType);
      bump(getUniqueId(key), taskIsBid);
      const category = tasksData[key]?._taskCategory;
      if (category) {
        bump(category, taskIsBid);
      }
    }
    return map;
  }, [tasksData, taskState]);

  const handleAddTask = (taskId: string) => {
    dispatch(addTaskByKey(taskId));
  };

  const handleSetAnswer =
    (taskId: string) => (questionKey: string, answers: string[]) => {
      dispatch(setTaskAnswer({ taskId, questionMapKey: questionKey, answers }));
    };

  const handleDeleteTask = (taskId: string) => {
    dispatch(deleteTaskByKey(taskId));
  };

  // Mobile opens the camera scoped to the task+label; on web the gallery panel
  // is the image-management surface, so a tile tap opens it.
  const handleSelectTaskLabel = () => {
    dispatch(setIsGalleryPanelOpen(true));
  };

  // Nothing to show when nothing has been added and the user can't add: with
  // no Add action (audit review) and no submitted tasks there is nothing to
  // display. When adding is allowed, also hide if the library is empty.
  if (addedTaskIds.length === 0 && (!allowAdd || !hasAvailableTasks)) {
    return null;
  }

  return (
    <Box sx={{ marginBottom: '16px' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '8px',
        }}
      >
        <CustomTypography
          variant='h5'
          sx={{
            color: '#343330',
            fontFamily: 'Montserrat',
            fontSize: '18px',
            fontWeight: 600,
            lineHeight: '21.94px',
          }}
        >
          {heading}
        </CustomTypography>
        {allowAdd && (
          <Button
            variant='outlined'
            startIcon={<AddIcon />}
            disabled={isReadOnly || !hasAvailableTasks}
            onClick={() => setPickerOpen(true)}
            sx={{
              borderRadius: '48px',
              border: '1px solid #E3E3E3',
              textTransform: 'none',
              fontFamily: 'Montserrat',
              fontWeight: 600,
              color: '#343330',
            }}
          >
            {addLabel}
          </Button>
        )}
      </Box>

      {allowAdd && (
        <TaskPickerDialog
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          title={addLabel}
          taskList={taskList ?? []}
          taskCountMap={taskCountMap}
          onAddTask={handleAddTask}
          disabled={isReadOnly}
        />
      )}

      {addedTaskIds.length === 0 && (
        <CustomTypography
          variant='body1'
          sx={{
            fontFamily: 'Montserrat',
            fontSize: '13px',
            color: '#666',
            fontWeight: 500,
          }}
        >
          {emptyText}
        </CustomTypography>
      )}

      {addedTaskIds.map((taskId) => {
        const task = tasksData[taskId];
        const questions = (taskState[taskId]?.questions ??
          []) as MobileStateQuestion[];
        const taskTitle = task?._taskName || task?.DisplayName || taskId;
        const taskSave = savedTasks?.[taskId];

        return (
          <Accordion
            key={taskId}
            id={taskAnchorId(taskId)}
            defaultExpanded
            elevation={0}
            sx={{
              border: '1px solid #E3E3E3',
              borderRadius: '8px',
              margin: '10px 0',
              '&::before': { height: '0px', backgroundColor: 'transparent' },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box
                sx={{
                  display: 'flex',
                  flex: 1,
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <CustomTypography
                  variant='h5'
                  sx={{
                    color: '#343330',
                    fontFamily: 'Montserrat',
                    fontSize: '16px',
                    fontWeight: 600,
                    textTransform: 'capitalize',
                  }}
                >
                  {keyToString(taskTitle)}
                </CustomTypography>
                {!isReadOnly && (
                  <Tooltip title='Remove task'>
                    <IconButton
                      size='small'
                      onClick={(event) => {
                        event.stopPropagation();
                        handleDeleteTask(taskId);
                      }}
                    >
                      <DeleteOutlineIcon fontSize='small' />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      display: 'flex',
                      flex: 6,
                      alignItems: 'center',
                      backgroundColor: '#EAEAEA',
                      borderRadius: '8px 8px 0 0',
                      height: '46px',
                    }}
                  >
                    <TableCell
                      sx={{
                        ...headerCommonSx,
                        flex: 3,
                        padding: '8px 16px',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      Question
                    </TableCell>
                    <TableCell sx={{ ...headerCommonSx, flex: 3 }}>
                      Value
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {questions.map((question, qIndex) => (
                    <TableRow
                      key={question.QuestionKey ?? qIndex}
                      sx={{
                        display: 'flex',
                        flex: 6,
                        alignItems: 'center',
                        backgroundColor: qIndex % 2 === 1 ? '#FBFBFB' : '#fff',
                      }}
                    >
                      <TableCell
                        sx={{
                          border: 'none',
                          flex: 3,
                          padding: '12px 16px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          fontFamily: 'Montserrat',
                          fontSize: '12px',
                          fontWeight: 500,
                        }}
                      >
                        {question?.Required && (
                          <span style={{ color: '#CB690E' }}>*</span>
                        )}
                        <CustomTypography
                          variant='body1'
                          sx={{
                            fontFamily: 'Montserrat',
                            fontSize: '12px',
                            fontWeight: 500,
                            color: '#343330',
                            textAlign: 'left',
                            textTransform: 'none',
                          }}
                        >
                          {keyToString(question?.DisplayText)}
                        </CustomTypography>
                      </TableCell>
                      <TableCell
                        sx={{ border: 'none', flex: 3, padding: '12px' }}
                      >
                        <QuestionItemView
                          question={question}
                          answers={taskSave?.Answers?.[question.QuestionKey]}
                          disabled={isReadOnly}
                          keyToString={keyToString}
                          onSetAnswer={handleSetAnswer(taskId)}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TaskMediaView
                taskId={taskId}
                onSelectLabel={handleSelectTaskLabel}
              />
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export default TaskControl;
